/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './TermOfServiceCard.styles';
import { useIntl } from 'react-intl';
import {
  Button,
  Checkbox,
  Input,
  InputNumber,
  message,
  Modal,
  Tooltip,
} from 'antd';
import React, {
  Fragment,
  useContext,
  useRef,
  useState,
  useEffect,
} from 'react';
import IconButton from '~components/_common/IconButton/IconButton';
import { UploadFileModal } from '~components/molecules/Tabs/TenderTabs/components/ContractAndOffers/ContractBuilder/ContractBuilderGroupCard/UploadFileModal/UploadFileModal';
import { PaperClipOutlined } from '@ant-design/icons';
import dataContextMenu, {
  DataContextMenuItemType,
} from '~src/data/dataContextMenu';
import { BaseRightClickOverlay } from '~components/atoms/RightClickOverlay/BaseRightClickOverlay';
import { useDraggableCard } from '~components/molecules/Tabs/TenderTabs/components/ContractAndOffers/ContractBuilder/ContractBuilderDnD/useDraggableCard';
import FileProgressContext, {
  FileProgress,
} from '~src/contexts/FileProgressContext';
import { UploadFile } from 'antd/es/upload/interface';
import { v4 as uuid } from 'uuid';
import { queryCache, useMutation } from 'react-query';
import {
  deleteVoucherAttachment,
  getVoucherAttachmentDownloadUrl,
} from '~src/services/api/contracts';
import { SimpleConfirmModal } from '~components/popups-modals/ConfirmModal/SimpleConfirmModal';
import moment from 'moment';
import { submitVoucherResponse } from '~src/services/api/shares';
import {
  getProjectVoucherAttachmentDownloadUrl,
  getVendorVoucherAttachmentDownloadUrl,
} from '~src/services/api';
import {
  renderOptionalVoucherBadge,
  renderVoucherTypeIcon,
} from '~components/molecules/Tabs/TenderTabs/components/ContractAndOffers/utils';
import { appLog } from '~src/utils/log';
import Viewer from '~components/atoms/Viewers/Viewer';

export interface TermOfServiceCardProps {
  id: string;
  type: 'card';
  subType: 4;
  cardTitle?: string;
  cardSubtitle?: string;
  cardQuantity?: number;
  cardPrice?: number;
  cardSum?: number;
  cardFiles: FileCardType[];
  borderless?: boolean;
  contractData: any;
  onContextMenu?: (e: React.MouseEvent) => void;
  onCardAction?: (action: string) => void;
  groupIndex?: number;
  onRightClick?: (e: React.MouseEvent) => void;
  setDraggingCardId?: (id: string | null) => void;
  isDragging?: boolean;
  isOnMyTender?: boolean;
  tenderClosed?: boolean;
  isOfferLocked?: boolean;
  cardCreationDate: number;
  responseData?: {
    voucher_id: number;
    sum: number | null;
    is_approved: boolean | null;
  } | null;
  contractDataMyTender: {
    shareId: string;
    shareKey: string;
    contractOfferData: {
      is_offer_created: boolean;
      offer_id: number;
      state: number;
      contract_id: number;
    };
  };
  isDeleted?: boolean;
  contractDeleted?: boolean;
  isOptional?: boolean;
}

type FileCardType = {
  id: string;
  name: string;
  description: string;
  file: File;
  size: number;
  serial_number: number;
  version: number;
  version_at: number;
  type?: string;
};

const TermOfServiceCard: React.FC<TermOfServiceCardProps> = ({
  id,
  subType,
  cardTitle,
  cardSubtitle,
  cardQuantity,
  cardPrice,
  cardSum,
  borderless,
  cardFiles,
  contractData,
  onContextMenu,
  onCardAction,
  groupIndex,
  onRightClick,
  setDraggingCardId,
  isDragging,
  isOnMyTender,
  tenderClosed,
  isOfferLocked,
  cardCreationDate,
  responseData,
  contractDataMyTender,
  isDeleted,
  contractDeleted,
  isOptional,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const { projectId, tenderId, contractId } = contractData;

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isAgree, setIsAgree] = useState<boolean>(false);

  const attachments = (cardFiles || []) as FileCardType[];
  const hasAttachment = attachments.length > 0;

  const [
    showConfirmDeleteAttachmentModal,
    setShowConfirmDeleteAttachmentModal,
  ] = useState(false);
  const [
    attachmentToDelete,
    setAttachmentToDelete,
  ] = useState<FileCardType | null>(null);

  // Drawer state for file preview
  const [previewDrawerVisible, setPreviewDrawerVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<{
    url: string;
    name: string;
    extension: string;
    dbFile?: FileCardType;
  } | null>(null);
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);

  const [showResponseModal, setShowResponseModal] = useState(false);
  const [isApproved, setIsApproved] = useState<boolean | null>(
    responseData?.is_approved ?? null
  );

  const [doSubmitResponse, { isLoading: isSubmitting }] = useMutation(
    submitVoucherResponse,
    {
      onSuccess: () => {
        message.success(intl.formatMessage({ id: 'response-saved' }));
        queryCache.invalidateQueries([
          'voucher-responses',
          contractDataMyTender.shareId,
          contractDataMyTender.shareKey,
          contractDataMyTender.contractOfferData.offer_id,
        ]);
        queryCache.invalidateQueries([
          'contracts',
          undefined,
          undefined,
          contractDataMyTender.shareId,
          contractDataMyTender.shareKey,
        ]);
        setShowResponseModal(false);
      },
      onError: () => {
        message.error(intl.formatMessage({ id: 'response-save-failed' }));
      },
    }
  );

  const handleSubmitVoucherResponse = async () => {
    if (tenderClosed || isOfferLocked || isDeleted || contractDeleted) return;

    appLog('submitVoucherResponse pressed', { isApproved });
    if (
      !contractDataMyTender.contractOfferData.offer_id ||
      !contractDataMyTender.shareId ||
      !contractDataMyTender.shareKey
    ) {
      message.error(intl.formatMessage({ id: 'error-missing-offer-data' }));
      return;
    }

    if (isApproved === null) {
      message.error(intl.formatMessage({ id: 'confirm-tos-before-submit' }));
      return;
    }

    await doSubmitResponse({
      share_id: contractDataMyTender.shareId,
      share_key: contractDataMyTender.shareKey,
      offer_id: contractDataMyTender.contractOfferData.offer_id,
      voucher_id: Number(id),
      data: { sum: null, is_approved: isApproved },
    });
  };

  const { setFileProgressContext, fileProgressContext } = useContext(
    FileProgressContext
  );

  const [doDeleteAttachment, { isLoading: isDeletingAttachment }] = useMutation(
    deleteVoucherAttachment,
    {
      onSuccess: () => {
        message.success(
          intl.formatMessage({ id: 'attachment-delete-successful' })
        );
        queryCache.invalidateQueries([projectId, tenderId, contractId]);
      },
      onError: () => {
        message.error(
          intl.formatMessage({ id: 'attachment-delete-successful' })
        );
      },
    }
  );

  // Refresh contracts after project-side upload finishes
  useEffect(() => {
    if (isOnMyTender || !projectId || !tenderId || !contractId || !id) {
      return;
    }

    const hasPendingUploads = fileProgressContext?.some(
      (f) =>
        f.voucherId === id &&
        f.contractId === contractId &&
        f.tenderId === tenderId &&
        !f.isDone
    );

    if (!hasPendingUploads) {
      queryCache.invalidateQueries([projectId, tenderId, contractId]);
    }
  }, [fileProgressContext, id, contractId, tenderId, projectId, isOnMyTender]);

  // Uploading files to API (project side only)
  const handleConfirmUpload = (fileList: UploadFile[]) => {
    if (tenderClosed || isOfferLocked || isDeleted || contractDeleted) return;

    setFileProgressContext((prev) => [
      ...prev,
      ...fileList.map(
        (file) =>
          //@ts-ignore
          ({
            id: uuid(),
            file: file.originFileObj!,
            shouldStartUpload: true,
            isDone: false,
            projectId: projectId,
            tenderId: tenderId,
            contractId: contractId,
            voucherId: id,
            entityCreated: false,
            commit: undefined,
            progress: 0,
          } as FileProgress)
      ),
    ]);

    setShowUploadModal(false);
  };

  // Confirm Delete Attachment Modal (project)
  const handleConfirmDeleteAttachment = async () => {
    if (tenderClosed || isOfferLocked || isDeleted || contractDeleted) return;
    if (!attachmentToDelete) return;

    await doDeleteAttachment({
      projectId,
      tenderId,
      contractId,
      voucherId: id,
      fileGuid: attachmentToDelete.id,
    });

    setAttachmentToDelete(null);
    setShowConfirmDeleteAttachmentModal(false);
  };

  const handleOpenUploadModal = () => {
    if (tenderClosed || isOfferLocked || isDeleted || contractDeleted) return;
    setShowUploadModal(true);
  };

  // Context Menu
  const handleContextMenuClick = (item: DataContextMenuItemType) => {
    if (tenderClosed || isOfferLocked || contractDeleted) return;

    switch (item.modal) {
      case 'delete':
        onCardAction?.('delete');
        break;
      case 'reactivate-card':
        onCardAction?.('reactivate-card');
        break;
      case 'edit-card':
        onCardAction?.('edit-card');
        break;
      case 'add-file':
        if (!hasAttachment && !isDeleted) {
          handleOpenUploadModal();
        }
        break;
    }
  };

  // DnD
  const cardRef = useRef<HTMLDivElement>(null);

  useDraggableCard(cardRef, id, {
    disabled:
      isOnMyTender ||
      tenderClosed ||
      isOfferLocked ||
      !!isDeleted ||
      !!contractDeleted,
    onDragStart: () => {
      if (!isOnMyTender) setDraggingCardId?.(id);
    },
    onDrop: () => {
      if (!isOnMyTender) setDraggingCardId?.(null);
    },
  });

  const dragDropTooltipTitle =
    !isOnMyTender &&
    intl.formatMessage({
      id: 'drag-and-drop-to-reorder',
    });

  const RightClickWrapper = !isOnMyTender ? BaseRightClickOverlay : Fragment;
  const wrapperProps = !isOnMyTender
    ? {
        items: (isDeleted
          ? dataContextMenu.deletedContractCard
          : dataContextMenu.contractCard
        ).map((item) => ({
          ...item,
          disabled:
            !isOnMyTender && (tenderClosed || isOfferLocked || contractDeleted)
              ? true
              : item.disabled,
        })),
        onItemClick: handleContextMenuClick,
        onOverlayClose: () => {
          //
        },
      }
    : {};

  // Vendor can download only when offer is created
  const canVendorDownload = !!contractDataMyTender.contractOfferData
    ?.is_offer_created;

  // Helper functions for drawer preview
  const isPreviewable = (extension: string) => {
    const ext = extension.toLowerCase();
    const isPdf = /^pdf$/i.test(ext);
    const isImage = /^(jpg|jpeg|png|gif|webp)$/i.test(ext);
    return isPdf || isImage;
  };

  const handleDrawerClose = () => {
    setPreviewDrawerVisible(false);
    setPreviewFile(null);
  };

  const handleFileDownload = () => {
    if (previewFile?.url) {
      const link = document.createElement('a');
      link.href = previewFile.url;
      link.download = previewFile.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleAttachmentPreview = async (file: FileCardType) => {
    const extension = file.name.split('.').pop() || '';
    if (!isPreviewable(extension)) return;

    // Open drawer immediately with empty URL (shows loading spinner)
    setPreviewFile({ url: '', name: file.name, extension, dbFile: file });
    setPreviewDrawerVisible(true);
    setIsLoadingUrl(true);

    try {
      let url: string;

      if (isOnMyTender) {
        // VENDOR SIDE viewing PROJECT-file
        const shareId = contractDataMyTender?.shareId;
        const shareKey = contractDataMyTender?.shareKey;
        const contractIdFromShare =
          contractDataMyTender?.contractOfferData?.contract_id;

        if (!shareId || !shareKey || !contractIdFromShare) {
          message.error(
            intl.formatMessage({
              id: 'error-missing-download-data',
            })
          );
          setPreviewDrawerVisible(false);
          setPreviewFile(null);
          return;
        }

        url = await getProjectVoucherAttachmentDownloadUrl({
          shareId,
          shareKey,
          contractId: contractIdFromShare,
          voucherId: id,
          fileGuid: file.id,
        });
      } else {
        // PROJECT SIDE viewing PROJECT-file
        url = await getVoucherAttachmentDownloadUrl({
          projectId,
          tenderId,
          contractId,
          voucherId: id,
          fileGuid: file.id,
        });
      }

      // Update with actual URL - Viewer will re-render with content
      setPreviewFile({ url, name: file.name, extension, dbFile: file });
    } catch (err) {
      message.error(
        intl.formatMessage({
          id: 'attachment-download-failed',
        })
      );
      setPreviewDrawerVisible(false);
      setPreviewFile(null);
    } finally {
      setIsLoadingUrl(false);
    }
  };

  return (
    <Fragment>
      <RightClickWrapper {...wrapperProps}>
        <div
          ref={cardRef}
          css={[
            styles.cardWrapper,
            borderless && { border: 'none' },
            isDragging && styles.cardGhost,
            isDeleted && {
              backgroundColor: '#fff2f0',
              border: '1px solid #ffccc7',
              opacity: 0.88,
            },
          ]}
          onContextMenu={onContextMenu}>
          {/* HEADER */}
          {cardTitle &&
            (isOnMyTender ? (
              <div
                css={{
                  display: 'flex',
                  flex: '5',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                <div css={{ width: '100%' }}>
                  <div
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap',
                    }}>
                    {isOptional && renderOptionalVoucherBadge(intl)}

                    <div css={[styles.cardTitle, { fontSize: '16px' }]}>
                      {cardTitle}
                    </div>
                  </div>
                  {cardSubtitle && (
                    <div
                      css={{
                        marginTop: 4,
                        color: 'gray',
                        fontSize: '14px',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                      }}>
                      {cardSubtitle}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div css={styles.cardTitleWrapper}>
                {/* Wrap Title and Icon in flex container */}
                <div
                  css={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {isOptional && renderOptionalVoucherBadge(intl)}

                  <div css={{ display: 'table', width: 'auto' }}>
                    <Tooltip
                      title={dragDropTooltipTitle}
                      placement="top"
                      mouseEnterDelay={0.5}
                      trigger={['hover']}>
                      <div
                        css={[
                          styles.cardTitle,
                          !cardSubtitle && { fontSize: '20px' },
                          { display: 'inline-block', width: 'auto' },
                        ]}>
                        {cardTitle}
                      </div>
                    </Tooltip>
                  </div>
                  {/* Voucher type icon for project side */}
                  {renderVoucherTypeIcon(subType, intl)}
                </div>
                <div
                  css={styles.cardSubTitle}
                  style={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}>
                  {cardSubtitle}
                </div>
              </div>
            ))}

          {/* ATTACHMENTS – shown for vendors and Project;
              delete only Project, download everywhere (except Vendor if offer is not created) */}
          <div css={styles.cardFileWrapper}>
            {attachments.length === 0 && (
              <span
                style={{
                  color: 'grey',
                  fontSize: '14px',
                  fontWeight: 'lighter',
                }}>
                {intl.formatMessage({ id: 'no-attachment-available' })}
              </span>
            )}

            {attachments.map((file, index) => {
              if (!file) return null;

              const maxNameLength = 18;
              const isTooLong = file.name.length > maxNameLength;
              const displayName = isTooLong
                ? file.name.slice(0, maxNameLength) + '…'
                : file.name;

              const isPdf =
                file.type === 'application/pdf' || file.name.endsWith('.pdf');

              const canDownload = !isOnMyTender || canVendorDownload;
              // && !tenderClosed; TV1-511

              return (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                    justifyContent: 'space-between',
                    padding: '5px 15px 5px 5px',
                  }}>
                  {isPdf ? (
                    <IconButton
                      icon={'file-pdf'}
                      iconStyle={'fad'}
                      color={'red'}
                      backgroundOff
                      customStyle={{ fontSize: '18px' }}
                      containerStyle={{
                        maxHeight: '18px',
                        maxWidth: '18px',
                        minHeight: '18px',
                        minWidth: '18px',
                      }}
                    />
                  ) : (
                    // @ts-ignore
                    <PaperClipOutlined style={{ fontSize: '18px' }} />
                  )}

                  <span style={{ fontWeight: 'lighter', fontSize: '14px' }}>
                    {displayName}
                  </span>

                  <div>
                    {/* Preview icon for previewable files */}
                    {isPreviewable(file.name.split('.').pop() || '') && (
                      <IconButton
                        icon={'eye'}
                        iconStyle={'far'}
                        color={'red'}
                        customStyle={{ fontSize: '16px' }}
                        click={() => handleAttachmentPreview(file)}
                      />
                    )}

                    {canDownload && (
                      <IconButton
                        icon={'download'}
                        iconStyle={'far'}
                        color={'blue'}
                        click={async () => {
                          // if (tenderClosed) return;

                          try {
                            let url: string;

                            if (isOnMyTender) {
                              // VENDOR SIDE download PROJECT-file
                              const shareId = contractDataMyTender?.shareId;
                              const shareKey = contractDataMyTender?.shareKey;
                              const contractIdFromShare =
                                contractDataMyTender?.contractOfferData
                                  ?.contract_id;

                              if (
                                !shareId ||
                                !shareKey ||
                                !contractIdFromShare
                              ) {
                                message.error(
                                  intl.formatMessage({
                                    id: 'error-missing-download-data',
                                  })
                                );
                                return;
                              }

                              url = await getProjectVoucherAttachmentDownloadUrl(
                                {
                                  shareId,
                                  shareKey,
                                  contractId: contractIdFromShare,
                                  voucherId: id,
                                  fileGuid: file.id,
                                }
                              );
                            } else {
                              // PROJECT SIDE download
                              url = await getVoucherAttachmentDownloadUrl({
                                projectId,
                                tenderId,
                                contractId,
                                voucherId: id,
                                fileGuid: file.id,
                              });
                            }

                            window.open(url, '_blank');
                          } catch (err) {
                            message.error(
                              intl.formatMessage({
                                id: 'attachment-download-failed',
                              })
                            );
                          }
                        }}
                        customStyle={{ fontSize: '16px' }}
                      />
                    )}

                    {!isOnMyTender && (
                      <IconButton
                        icon={'trash'}
                        iconStyle={'far'}
                        color={
                          tenderClosed ||
                          isOfferLocked ||
                          isDeleted ||
                          contractDeleted
                            ? 'grey'
                            : 'red'
                        }
                        click={() => {
                          if (
                            tenderClosed ||
                            isOfferLocked ||
                            isDeleted ||
                            contractDeleted
                          )
                            return;
                          setAttachmentToDelete(file);
                          setShowConfirmDeleteAttachmentModal(true);
                        }}
                        customStyle={{
                          fontSize: '16px',
                          cursor:
                            !isOnMyTender &&
                            (tenderClosed ||
                              isOfferLocked ||
                              isDeleted ||
                              contractDeleted)
                              ? 'not-allowed'
                              : 'pointer',
                          opacity:
                            !isOnMyTender &&
                            (tenderClosed ||
                              isOfferLocked ||
                              isDeleted ||
                              contractDeleted)
                              ? 0.5
                              : 1,
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Upload Button – Project side */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {!isOnMyTender && (
              <Button
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={handleOpenUploadModal}
                disabled={
                  hasAttachment ||
                  !!tenderClosed ||
                  !!isOfferLocked ||
                  !!isDeleted ||
                  !!contractDeleted
                }>
                <IconButton
                  icon={'up-from-bracket'}
                  iconStyle={'far'}
                  backgroundOff
                  customStyle={{ fontSize: '16px' }}
                />
                <span>{intl.formatMessage({ id: 'upload' })}</span>
              </Button>
            )}
          </div>

          {/* VENDOR: Response Status + Button */}
          {contractDataMyTender.contractOfferData?.is_offer_created && (
            <Fragment>
              <div
                css={{
                  flex: 1, // Changed from 2 to 1 to work as a spacer
                  textAlign: 'right', // Align text to right like "Vendor File"
                  paddingRight: '15px',
                  color: '#555',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}>
                {responseData && responseData.is_approved !== null ? (
                  <div>
                    {intl.formatMessage({ id: 'accepted-label' })}{' '}
                    <b>
                      {responseData.is_approved
                        ? intl.formatMessage({ id: 'yes' })
                        : intl.formatMessage({ id: 'no' })}
                    </b>
                  </div>
                ) : (
                  <div style={{ color: '#999', whiteSpace: 'nowrap' }}>
                    {intl.formatMessage({ id: 'no-response-yet' })}
                  </div>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                <Button
                  type="primary"
                  disabled={
                    !!tenderClosed ||
                    !!isOfferLocked ||
                    !!isDeleted ||
                    !!contractDeleted
                  }
                  onClick={() => {
                    if (
                      tenderClosed ||
                      isOfferLocked ||
                      isDeleted ||
                      contractDeleted
                    )
                      return;
                    setShowResponseModal(true);
                  }}>
                  {responseData && responseData.is_approved !== null
                    ? intl.formatMessage({ id: 'edit-response' })
                    : intl.formatMessage({ id: 'set-response' })}
                </Button>
              </div>
            </Fragment>
          )}

          {isOnMyTender && (
            <div
              css={{
                textAlign: 'right',
                color: 'gray',
                fontSize: '12px',
              }}>
              <div>
                {/* {moment.unix(cardCreationDate).format('DD/MM/YYYY - HH:mm')} TV1-513 */}
              </div>
              <div style={{ marginTop: '4px' }}>
                {/* Replace type text with icon utility */}
                {renderVoucherTypeIcon(subType, intl)}
              </div>
            </div>
          )}

          {/* Kebab – Project */}
          {!isOnMyTender && (
            <div css={styles.cardOptionWrapper}>
              <IconButton
                icon={['far', 'ellipsis-v']}
                widthTooltip={intl.formatMessage({ id: 'settings' })}
                customStyle={{
                  cursor:
                    !isOnMyTender &&
                    (tenderClosed || isOfferLocked || contractDeleted)
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    !isOnMyTender &&
                    (tenderClosed || isOfferLocked || contractDeleted)
                      ? 0.5
                      : 1,
                }}
                click={(e) => {
                  onRightClick?.(e);
                  const fakeEvent = new MouseEvent('contextmenu', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    button: 2,
                    buttons: 2,
                    clientX: e.clientX,
                    clientY: e.clientY,
                  });
                  e.currentTarget.dispatchEvent(fakeEvent);
                }}
              />
            </div>
          )}
        </div>
      </RightClickWrapper>

      {/* MODALS */}
      <UploadFileModal
        maxFiles={1}
        visible={showUploadModal}
        onCancel={() => {
          setShowUploadModal(false);
        }}
        onConfirm={handleConfirmUpload}
      />

      <SimpleConfirmModal
        visible={showConfirmDeleteAttachmentModal}
        isLoading={isDeletingAttachment}
        okButtonProps={{
          disabled:
            !!tenderClosed ||
            !!isOfferLocked ||
            !!isDeleted ||
            !!contractDeleted,
        }}
        onOk={handleConfirmDeleteAttachment}
        onCancel={() => {
          setAttachmentToDelete(null);
          setShowConfirmDeleteAttachmentModal(false);
        }}
        modalTitle={`${intl.formatMessage({ id: 'delete-attachment' })} - ${
          attachmentToDelete?.name
        }`}
        modalIcon={['far', 'trash']}
        okText={intl.formatMessage({ id: 'delete' })}
        okType="danger"
      />

      {/* Vendor response – "I agree" */}
      <Modal
        title={intl.formatMessage({ id: 'terms-of-service' })}
        visible={showResponseModal}
        confirmLoading={isSubmitting}
        okButtonProps={{
          disabled:
            !!tenderClosed ||
            !!isOfferLocked ||
            !!isDeleted ||
            !!contractDeleted,
        }}
        cancelButtonProps={{
          disabled:
            !!tenderClosed ||
            !!isOfferLocked ||
            !!isDeleted ||
            !!contractDeleted,
        }}
        onOk={handleSubmitVoucherResponse}
        onCancel={() => setShowResponseModal(false)}>
        <p style={{ marginBottom: 12 }}>
          {intl.formatMessage({ id: 'tos-modal-description' })}
        </p>
        <Checkbox
          checked={!!isApproved}
          disabled={
            !!tenderClosed ||
            !!isOfferLocked ||
            !!isDeleted ||
            !!contractDeleted
          }
          onChange={(e) => setIsApproved(e.target.checked)}>
          {intl.formatMessage({ id: 'tos-accept-checkbox' })}
        </Checkbox>
      </Modal>

      {/* Drawer preview */}
      {previewFile && (
        <Viewer
          mode="drawer"
          drawerVisible={previewDrawerVisible}
          onDrawerClose={handleDrawerClose}
          drawerTitle={previewFile.name}
          drawerWidth={800}
          fileUrl={previewFile.url}
          fileExtension={previewFile.extension}
          showDownloadButton={true}
          onDownload={handleFileDownload}
          isLoadingUrl={isLoadingUrl}
        />
      )}
    </Fragment>
  );
};

export default TermOfServiceCard;
